import type { AxiosResponse } from 'axios';
import { contentDisposition } from '@tinyhttp/content-disposition';
import axios from 'axios';

import type { ApiClient, IApiJsonRequest } from '../../ApiClient';
import type {
  IApiFilesUploadRequestBody,
  IApiFilesUploadResponseBody,
  IApiFileUploadProgressEvent,
} from './FilesApi.types';

export class FilesApi {
  public constructor(private client: ApiClient) {}

  public async prepareForUpload(
    appId: string | undefined,
    body: IApiFilesUploadRequestBody,
  ): Promise<IApiFilesUploadResponseBody> {
    const request: IApiJsonRequest = {
      path: '/files',
      method: 'post',
      body: body,
    };

    const response = await this.client.fetchJson<IApiFilesUploadResponseBody>(
      request,
      { appId },
    );

    return response.body;
  }

  public async upload(
    file: File,
    toUrl: string,
    onUploadProgress?: (progressEvent: IApiFileUploadProgressEvent) => void,
    abortController?: AbortController,
  ): Promise<AxiosResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const data = reader.result;
        try {
          const response = await axios.request({
            method: 'put',
            url: toUrl,
            data,
            headers: {
              'content-type': file.type,
              'content-disposition': contentDisposition(file.name),
            },
            signal: abortController?.signal,
            onUploadProgress: (progressEvent) => {
              onUploadProgress?.({
                loading: progressEvent.progress !== 1,
                progress: progressEvent.progress
                  ? Math.min(progressEvent.progress, 0.99)
                  : undefined,
              });
            },
          });

          resolve(response);
        } catch (error) {
          reject(error as Error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }
}
